<template>
  <div class="tome-ogt-container">
    <div class="content-section">
      <h1>Optimal Growth Temperature Prediction (Tome OGT)</h1>
      
      <div class="description">
        <h2>About Tome OGT</h2>
        <p>Tome OGT is a tool that uses machine learning algorithms to predict the Optimal Growth Temperature (OGT) of microorganisms. By analyzing protein sequence features, it can accurately estimate the temperature preferences of microorganisms.</p>
        
        <p class="additional-info">
          Tome OGT is based on amino acid composition and other features of the proteome, using machine learning models to predict the optimal growth temperature of microorganisms. This is especially important for studying the growth characteristics of methanogens and other specialized environmental microorganisms. This tool can be used for:
        </p>
        <ul class="feature-list">
          <li><strong>Environmental Adaptation Research:</strong> Understanding microbial adaptation mechanisms to temperature</li>
          <li><strong>Microbial Ecology:</strong> Studying microbial distribution in different environments</li>
          <li><strong>Industrial Applications:</strong> Screening microorganisms that function at specific temperatures</li>
          <li><strong>Evolutionary Studies:</strong> Analyzing patterns of temperature adaptation</li>
        </ul>
        
        <el-alert
          title="Processing Time Notice"
          type="info"
          :closable="false"
          show-icon
        >
          <p>Analysis may take several minutes, depending on the size of the proteome sequence. Please be patient.</p>
        </el-alert>
      </div>

      <!-- Add batch analysis results section -->
      <div v-if="batchResults.batchJobId" class="batch-results-section">
        <h2>Batch Analysis Results</h2>
        
        <div v-if="!batchResults.isCompleted" class="batch-progress">
          <el-progress 
            :percentage="batchProgress" 
            :status="batchResults.hasError ? 'exception' : ''" 
            :format="(p) => `${batchResults.processedCount}/${batchResults.totalCount} (${p}%)`"
          />
          <p>Analysis in progress, please wait...</p>
          <el-button @click="refreshBatchStatus" size="small" :loading="refreshingStatus">
            Refresh Status
          </el-button>
        </div>
        
        <div v-if="batchResults.hasError" class="batch-error">
          <el-alert
            title="Error occurred during batch analysis"
            type="error"
            :closable="false"
            show-icon
          >
            <p>{{ batchResults.errorMessage }}</p>
          </el-alert>
        </div>
        
        <el-table
          v-if="batchResults.resultsList.length > 0"
          :data="batchResults.resultsList"
          style="width: 100%"
          :default-sort="{prop: 'predOGT', order: 'descending'}"
          border
          stripe
        >
          <el-table-column prop="organismName" label="Strain Name" min-width="200" show-overflow-tooltip>
            <template #default="scope">
              <span :title="scope.row.assembly_accession">{{ scope.row.organismName }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="accession" label="Assembly Accession" min-width="150" show-overflow-tooltip />
          <el-table-column prop="predOGT" label="Predicted OGT (°C)" min-width="120" sortable>
            <template #default="scope">
              <span :class="{'highlight-temp': scope.row.predOGT > 60}">
                {{ scope.row.predOGT.toFixed(2) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="Status" min-width="100">
            <template #default="scope">
              <el-tag :type="scope.row.status === 'success' ? 'success' : 'danger'">
                {{ scope.row.status === 'success' ? 'Success' : 'Failed' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="category" label="Category" min-width="120">
            <template #default="scope">
              <el-tag :type="getTempTagType(scope.row.predOGT)">
                {{ getTempCategory(scope.row.predOGT) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Actions" min-width="100" fixed="right">
            <template #default="scope">
              <el-button
                v-if="scope.row.status === 'success'"
                size="small"
                type="primary"
                @click="downloadResult(scope.row.jobId)"
                :disabled="!scope.row.jobId"
              >
                Download
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="upload-section">
        <h2>Upload Proteome File</h2>
        <p class="upload-info">Please upload a proteome file in FASTA format (.fa, .fasta, .faa). File size limit: 50MB.</p>
        
        <el-upload
          class="upload-demo"
          drag
          action="/api/tools/tome/upload/"
          :on-success="handleSuccess"
          :on-error="handleError"
          :before-upload="beforeUpload"
          :on-progress="handleProgress"
          :multiple="false"
          :limit="1"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            Drop file here or <em>click to upload</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              Supported file formats: .fa, .fasta, .faa (max 50MB)
            </div>
          </template>
        </el-upload>

        <!-- Upload progress display -->
        <div v-if="uploading" class="progress-section">
          <el-progress 
            :percentage="uploadProgress" 
            :status="uploadProgress === 100 ? 'success' : ''"
            :format="progressFormat"
          />
          <p class="progress-text">{{ progressText }}</p>
        </div>
        
        <!-- Start analysis button -->
        <div v-if="showAnalysisOptions" class="start-analysis-section">
          <el-button 
            type="primary" 
            @click="startAnalysis" 
            :loading="analyzing"
            :disabled="!canAnalyze"
            class="start-analysis-button"
          >
            Start Analysis
          </el-button>
        </div>
      </div>

      <!-- Results display area -->
      <div v-if="results" class="results-section">
        <div class="results-header">
          <h2>Analysis Results</h2>
          <el-button 
            type="primary" 
            @click="downloadResults" 
            :disabled="!resultFileUrl"
            :loading="downloading"
          >
            Download Results (CSV)
          </el-button>
        </div>

        <el-card class="ogt-result-card">
          <template #header>
            <div class="card-header">
              <span>Predicted Optimal Growth Temperature (OGT)</span>
            </div>
          </template>
          <div v-if="results.warningMessage" class="warning-message">
            <el-alert
              :title="results.warningMessage"
              type="warning"
              :closable="false"
              show-icon
            />
          </div>
          <div class="temperature-display">
            <div class="temperature-value">
              <span class="value">{{ results.predOGT }}</span>
              <span class="unit">°C</span>
            </div>
            <div class="temperature-gauge">
              <el-progress 
                type="dashboard" 
                :percentage="getTempPercentage(results.predOGT)" 
                :color="getTempColor(results.predOGT)"
                :stroke-width="15"
              />
              <div class="percentage-explanation">
                Indicates the relative position of the current temperature in the 0-120°C range
              </div>
            </div>
          </div>
          <div class="temperature-category">
            <el-tag :type="getTempTagType(results.predOGT)" size="large">
              {{ getTempCategory(results.predOGT) }}
            </el-tag>
          </div>
          <div class="additional-info">
            <p><strong>Organism Type:</strong> {{ results.organismType || "Unknown" }}</p>
            <p><strong>Analysis Completion Time:</strong> {{ results.analysisTime || formatDate(new Date()) }}</p>
          </div>
        </el-card>
      </div>

      <div v-if="analyzing" class="analyzing-section">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>Analyzing...</span>
            </div>
          </template>
          <div class="analyzing-progress">
            <el-progress 
              type="circle" 
              :percentage="analysisProgress" 
              :indeterminate="analysisProgress === 0"
              :status="analysisProgress === 100 ? 'success' : 'warning'"
            />
            <p class="analyzing-text">{{ analysisText }}</p>
          </div>
        </el-card>
      </div>
    </div>

    <!-- Citation section -->
    <div class="citation-section">
      <h3>Citation Information</h3>
      <div class="citation-content">
        <p class="citation-text">
          This tool integrates Tome OGT, developed by Li G et al. If you use this tool in your research, please cite the following reference:
        </p>
        <div class="citation-box">
          <p class="paper-citation">
            <strong>Primary Citation:</strong><br>
            Li G., et al., (2019). Machine learning applied to predicting microorganism growth temperatures and enzyme catalytic optima.
            <em>ACS Synthetic Biology</em>, 8, 1411–1420.
            <a href="https://doi.org/10.1021/acssynbio.9b00099" target="_blank">https://doi.org/10.1021/acssynbio.9b00099</a>
          </p>
          <p class="repo-citation">
            <strong>Software Repository:</strong><br>
            Tome GitHub Repository: <a href="https://github.com/EngqvistLab/Tome" target="_blank">https://github.com/EngqvistLab/Tome</a>
          </p>
        </div>
        <div class="license-info">
          <p class="license-text">
            Tome is licensed under the GNU General Public License v3.0 (GPL-3.0).
          </p>
          <p class="usage-note">
            This tool is an integrated implementation of Tome, not an official product of the Tome project. Please visit the Tome GitHub repository for the latest updates and official implementation.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { UploadFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

export default {
  name: 'TomeOGT',
  components: {
    UploadFilled
  },
  setup() {
    const uploading = ref(false)
    const uploadProgress = ref(0)
    const progressText = ref('')
    const analyzing = ref(false)
    const analysisProgress = ref(0)
    const analysisText = ref('Analyzing proteome sequence features...')
    const results = ref(null)
    const resultFileUrl = ref(null)
    const downloading = ref(false)
    const showAnalysisOptions = ref(false)
    const uploadedFilePath = ref(null)
    const analysisOptions = ref({
      sequenceType: 'protein'
    })
    const canAnalyze = computed(() => !!uploadedFilePath.value)
    const batchResults = ref({
      batchJobId: null,
      totalCount: 0,
      processedCount: 0,
      isCompleted: false,
      hasError: false,
      errorMessage: '',
      resultsList: []
    })
    const batchProgress = ref(0)
    const refreshingStatus = ref(false)

    const beforeUpload = (file) => {
      const isValidType = ['fa', 'fasta', 'faa'].includes(
        file.name.split('.').pop().toLowerCase()
      )
      const isLt50M = file.size / 1024 / 1024 < 50

      if (!isValidType) {
        ElMessage.error('Please upload a FASTA format file (.fa, .fasta, .faa)!')
        return false
      }
      if (!isLt50M) {
        ElMessage.error('File size cannot exceed 50MB!')
        return false
      }
      uploading.value = true
      uploadProgress.value = 0
      progressText.value = 'Preparing upload...'
      return true
    }

    const handleProgress = (event) => {
      uploadProgress.value = Math.round(event.percent)
      if (uploadProgress.value < 100) {
        progressText.value = `Upload progress: ${uploadProgress.value}%`
      } else {
        progressText.value = 'Processing...'
      }
    }

    const progressFormat = (percentage) => {
      if (percentage === 100) {
        return 'Processing'
      }
      return `${percentage}%`
    }

    const handleSuccess = (response) => {
      uploading.value = false
      showAnalysisOptions.value = true
      
      if (response.status === 'success') {
        ElMessage.success('File uploaded successfully')
        uploadedFilePath.value = response.file_path
      } else {
        ElMessage.error(response.message || 'File upload failed')
      }
    }

    const handleError = (error) => {
      uploading.value = false
      showAnalysisOptions.value = false
      console.error('Upload error:', error)
      
      let errorMessage = 'File upload failed';
      
      if (error.response) {
        const responseData = error.response.data;
        errorMessage = responseData.message || responseData.error || errorMessage;
      }
      
      ElMessage.error(errorMessage)
    }

    const startAnalysis = async () => {
      if (!uploadedFilePath.value) {
        ElMessage.warning('Please upload a proteome file first')
        return
      }

      analyzing.value = true
      analysisProgress.value = 10
      analysisText.value = 'Analyzing proteome sequence features...'
      
      try {
        const response = await fetch('/api/tools/tome/analyze/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            file_path: uploadedFilePath.value,
            sequence_type: analysisOptions.value.sequenceType
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || errorData.error || 'Analysis failed')
        }

        const data = await response.json()
        
        analysisProgress.value = 100
        analyzing.value = false
        
        if (data.status === 'success') {
          // Check if predOGT value is reasonable
          const predOGT = parseFloat(data.results.predOGT)
          if (isNaN(predOGT) || predOGT <= 0) {
            console.error('Received invalid OGT value:', data.results.predOGT)
            ElMessage.warning('Received invalid prediction result, may need to re-analyze')
          }
          
          results.value = {
            predOGT: predOGT.toFixed(2),
            organismType: getOrganismType(predOGT),
            analysisTime: formatDate(new Date())
          }
          
          // If result is 0, display more detailed warning
          if (predOGT <= 0) {
            results.value.warningMessage = 'Predicted value is 0 or negative, which may indicate a problem with the analysis. Please check if your input file is correct.'
          }
          
          // If server returns warning message, display to user
          if (data.warning) {
            results.value.warningMessage = data.warning
            console.warn('Server warning:', data.warning)
          }
          
          resultFileUrl.value = data.results.result_file
          ElMessage.success('Analysis completed')
        } else {
          throw new Error(data.message || 'Analysis failed')
        }
      } catch (error) {
        console.error('Analysis error:', error)
        analyzing.value = false
        analysisProgress.value = 0
        ElMessage.error({
          message: 'Analysis failed: ' + error.message,
          duration: 5000
        })
        
        // Display error handling guide
        results.value = {
          predOGT: '0.00',
          organismType: 'Unknown',
          analysisTime: formatDate(new Date()),
          warningMessage: `Analysis failed: ${error.message}. Possible reasons: 1) Incorrect file format 2) WSL environment configuration issue 3) Server error. Please check your uploaded file format and try again, or contact the administrator.`
        }
      }
    }

    const getOrganismType = (temp) => {
      if (temp < 20) return 'Psychrophile'
      if (temp < 45) return 'Mesophile'
      if (temp < 80) return 'Thermophile'
      return 'Hyperthermophile'
    }

    const getTempPercentage = (temp) => {
      // Map temperature to 0-100 range
      const min = 0
      const max = 120
      return Math.min(100, Math.max(0, Math.round((temp - min) / (max - min) * 100)))
    }

    const getTempColor = (temp) => {
      if (temp < 20) return '#409EFF' // Blue - Psychrophile
      if (temp < 45) return '#67C23A' // Green - Mesophile
      if (temp < 80) return '#E6A23C' // Yellow - Thermophile
      return '#F56C6C' // Red - Hyperthermophile
    }

    const getTempTagType = (temp) => {
      if (temp < 20) return 'info' // Blue - Psychrophile
      if (temp < 45) return 'success' // Green - Mesophile
      if (temp < 80) return 'warning' // Yellow - Thermophile
      return 'danger' // Red - Hyperthermophile
    }

    const getTempCategory = (temp) => {
      if (temp < 20) return 'Psychrophile'
      if (temp < 45) return 'Mesophile' 
      if (temp < 80) return 'Thermophile'
      return 'Hyperthermophile'
    }

    const formatDate = (date) => {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(date)
    }

    const downloadResults = async () => {
      if (!resultFileUrl.value) {
        ElMessage.warning('No result file available for download')
        return
      }
      
      downloading.value = true
      try {
        const response = await fetch(resultFileUrl.value)
        
        if (!response.ok) {
          throw new Error('Download failed')
        }
        
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `tome_ogt_${formatDateForFilename(new Date())}.csv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        
        ElMessage.success('Download successful')
      } catch (error) {
        console.error('Download error:', error)
        ElMessage.error('Download failed, please try again')
      } finally {
        downloading.value = false
      }
    }

    const formatDateForFilename = (date) => {
      return date.toISOString().slice(0, 10).replace(/-/g, '')
    }

    const refreshBatchStatus = async () => {
      refreshingStatus.value = true
      try {
        // First, ensure there is a batch job ID
        const batchJobId = batchResults.value?.batchJobId
        if (!batchJobId) {
          throw new Error('No batch job ID found')
        }
        
        // Fix URL format
        const response = await fetch(`/api/tools/tome/batch_status/${batchJobId}/`, {
          method: 'GET',
        })

        if (!response.ok) {
          throw new Error('Failed to retrieve batch analysis status')
        }

        const data = await response.json()
        
        if (data.status === 'success' && data.batch_info) {
          // Update processing progress
          const batchInfo = data.batch_info
          batchResults.value.processedCount = batchInfo.processed
          batchResults.value.totalCount = batchInfo.total_strains
          
          // Calculate progress percentage
          batchProgress.value = Math.floor((batchInfo.processed / batchInfo.total_strains) * 100)
          
          // Check for errors or completion
          if (batchInfo.status === 'error') {
            batchResults.value.hasError = true
            batchResults.value.errorMessage = batchInfo.error_message || 'Batch processing task error'
          } else if (batchInfo.status === 'completed' || batchInfo.processed >= batchInfo.total_strains) {
            // Processing complete
            batchResults.value.isCompleted = true
            
            // Update results list
            updateResultsList(batchInfo)
          }
        }
        
        return Promise.resolve()
      } catch (error) {
        console.error('Error getting batch status:', error);
        batchResults.value.hasError = true;
        batchResults.value.errorMessage = `Error getting status: ${error.message}`;
        
        return Promise.reject(error)
      } finally {
        refreshingStatus.value = false
      }
    }

    // Add function to update results list
    const updateResultsList = (batchInfo) => {
      try {
        // Read strain information from batch task
        const strainInfoJson = localStorage.getItem('tome_batch_strains')
        let strainInfo = []
        try {
          strainInfo = JSON.parse(strainInfoJson || '[]')
        } catch (e) {
          console.error('Failed to parse strain info:', e)
        }
        
        // Process result data
        const resultsList = []
        for (const [accession, result] of Object.entries(batchInfo.results)) {
          // Find corresponding strain information
          const strainData = strainInfo.find(s => s.assembly_accession === accession) || {}
          
          resultsList.push({
            accession: accession,
            organismName: strainData.organism_name || accession,
            assemblyName: strainData.assembly_name || '',
            predOGT: parseFloat(result.predOGT) || 0,
            confidence: result.confidence || 0,
            status: result.status || 'error',
            jobId: result.job_id || '',
            message: result.message || ''
          })
        }
        
        // Sort by predicted temperature in descending order
        resultsList.sort((a, b) => b.predOGT - a.predOGT)
        batchResults.value.resultsList = resultsList
        
        // Display notification after analysis completion
        ElMessage.success('Batch analysis completed')
      } catch (error) {
        console.error('Error processing results list:', error)
        batchResults.value.hasError = true
        batchResults.value.errorMessage = `Error processing results list: ${error.message}`
      }
    }

    const downloadResult = async (jobId) => {
      if (!jobId) {
        ElMessage.warning('No job ID available for download')
        return
      }
      
      downloading.value = true
      try {
        const response = await fetch(`/api/tools/tome/download/${jobId}/`)
        
        if (!response.ok) {
          throw new Error('Download failed')
        }
        
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `tome_ogt_${formatDateForFilename(new Date())}_${jobId}.csv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        
        ElMessage.success('Download successful')
      } catch (error) {
        console.error('Download error:', error)
        ElMessage.error('Download failed, please try again')
      } finally {
        downloading.value = false
      }
    }

    // Initialize to check batch analysis tasks
    onMounted(() => {
      console.log('TomeOGT component mounted, checking for batch analysis tasks...');
      
      // Check if there is a batch task ID in localStorage
      const batchJobId = localStorage.getItem('tome_batch_job_id');
      console.log('Batch job ID retrieved from localStorage:', batchJobId);
      
      if (batchJobId) {
        // Read strain information for the batch task
        const strainInfoJson = localStorage.getItem('tome_batch_strains');
        console.log('Strain information retrieved from localStorage:', strainInfoJson);
        
        let strainInfo = [];
        try {
          strainInfo = JSON.parse(strainInfoJson || '[]');
          console.log('Parsed strain information:', strainInfo);
        } catch (e) {
          console.error('Failed to parse strain information:', e);
        }
        
        // Display a notification message
        ElMessage({
          message: `Loading OGT analysis results for ${strainInfo.length} strains...`,
          type: 'info',
          duration: 3000
        });
        
        // Initialize batch results object
        batchResults.value = {
          batchJobId: batchJobId,
          totalCount: strainInfo.length,
          processedCount: 0,
          isCompleted: false,
          hasError: false,
          errorMessage: '',
          resultsList: []
        };
        
        // Execute status check immediately
        refreshBatchStatus();
        
        // Define polling function
        const pollStatus = () => {
          console.log('Executing polling to check batch analysis status...');
          refreshBatchStatus().then(() => {
            // If not yet complete and no errors, continue polling
            if (!batchResults.value.isCompleted && !batchResults.value.hasError) {
              console.log('Analysis not complete, polling again in 5 seconds...');
              setTimeout(pollStatus, 5000);
            } else if (batchResults.value.isCompleted) {
              console.log('Analysis complete, clearing localStorage data');
              // Clear localStorage data after analysis completion
              localStorage.removeItem('tome_batch_job_id');
              localStorage.removeItem('tome_batch_strains');
              
              // Display completion message
              ElMessage.success('Batch analysis complete!');
            }
          }).catch(error => {
            console.error('Polling error:', error);
            // Continue polling even if there's an error
            setTimeout(pollStatus, 10000);
          });
        };
        
        // Start polling
        setTimeout(pollStatus, 3000); // Delay polling start by 3 seconds to give the first refreshBatchStatus time to complete
      } else {
        console.log('No batch analysis task ID found');
      }
    });

    return {
      uploading,
      uploadProgress,
      progressText,
      analyzing,
      analysisProgress,
      analysisText,
      results,
      resultFileUrl,
      downloading,
      showAnalysisOptions,
      analysisOptions,
      canAnalyze,
      beforeUpload,
      handleProgress,
      handleSuccess,
      handleError,
      progressFormat,
      startAnalysis,
      getTempPercentage,
      getTempColor,
      getTempTagType,
      getTempCategory,
      formatDate,
      downloadResults,
      batchResults,
      batchProgress,
      refreshingStatus,
      refreshBatchStatus,
      downloadResult
    }
  }
}
</script>

<style scoped>
.tome-ogt-container {
  padding: 20px;
}

.content-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}

h1 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.description {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.description h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.description p {
  color: #666;
  line-height: 1.6;
}

.feature-list {
  list-style-type: none;
  padding-left: 0;
}

.feature-list li {
  color: #666;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.feature-list li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.upload-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.upload-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.upload-info {
  color: #666;
  margin-bottom: 20px;
}

.progress-section {
  margin-top: 20px;
}

.progress-text {
  text-align: center;
  color: #666;
  margin-top: 10px;
}

.start-analysis-section {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.start-analysis-button {
  padding: 12px 30px;
  font-size: 16px;
}

.results-section {
  margin-top: 30px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.results-header h2 {
  color: #2c3e50;
  font-size: 1.5rem;
  margin: 0;
}

.ogt-result-card {
  margin-bottom: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f5f7fa;
  padding: 15px 20px;
}

.temperature-display {
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin: 30px 0;
  flex-wrap: wrap;
}

.temperature-value {
  display: flex;
  align-items: baseline;
  margin-bottom: 20px;
}

.value {
  font-size: 4rem;
  font-weight: bold;
  color: #2c3e50;
  line-height: 1;
}

.unit {
  font-size: 1.8rem;
  margin-left: 5px;
  color: #606266;
}

.temperature-gauge {
  width: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.percentage-explanation {
  font-size: 12px;
  color: #909399;
  text-align: center;
  margin-top: 8px;
  max-width: 150px;
  line-height: 1.4;
}

.temperature-category {
  margin: 20px 0;
  text-align: center;
}

.temperature-category .el-tag {
  padding: 8px 16px;
  font-size: 16px;
}

.additional-info {
  margin: 25px 20px;
  color: #606266;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
}

.additional-info p {
  margin: 10px 0;
  line-height: 1.5;
}

.analyzing-section {
  margin-top: 30px;
}

.analyzing-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 20px 0;
}

.analyzing-text {
  margin-top: 20px;
  color: #606266;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 200px;
}

:deep(.el-upload__tip) {
  color: #666;
}

:deep(.el-progress-bar__inner) {
  transition: width 0.3s ease;
}

.citation-section {
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.citation-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.3rem;
}

.citation-content {
  color: #666;
  line-height: 1.6;
}

.citation-box {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin: 10px 0;
}

.citation-box a {
  color: #409EFF;
  text-decoration: none;
}

.citation-box a:hover {
  text-decoration: underline;
}

.license-text {
  margin-top: 10px;
  font-style: italic;
}

.license-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.usage-note {
  margin-top: 10px;
  font-size: 0.9em;
  color: #909399;
}

.paper-citation {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e4e7ed;
  line-height: 1.6;
}

.paper-citation em {
  font-style: italic;
}

.repo-citation {
  margin-bottom: 0;
  line-height: 1.6;
}

.citation-box strong {
  color: #2c3e50;
}

.warning-message {
  margin-bottom: 20px;
}

.batch-results-section {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.batch-results-section h2 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.5rem;
}

.batch-progress {
  margin-bottom: 20px;
}

.batch-progress p {
  margin-bottom: 10px;
}

.batch-error {
  margin-bottom: 20px;
}

.batch-error .el-alert {
  margin-bottom: 10px;
}

.el-table {
  margin-top: 20px;
}

.el-table .el-tag {
  margin-right: 5px;
}

.el-table .highlight-temp {
  color: #409EFF;
}
</style> 